import React, { useContext, useEffect, useMemo } from "react";
import { useLocation, Link, Navigate, useSearchParams } from "react-router-dom";
import { Button, Icon, Typography } from "@mui/material";
import { GlobalContext } from "@/helpers/contexts/contexts";

import ThankYou from "../components/thank-you/ThankYou";
import PageLayout from "@/components/layouts/page/PageLayout";
import CustomizeAds from "../components/thank-you/CustomizeAds";
import CampaignConfirmationThankyouPage from "../components/thank-you/CampaignConfirmationThankyouPage";
import BoostCampaignConfirmationThankyouPage from "../components/thank-you/BoostCampaignConfirmationThankyouPage";
import CampaignPauseSuccess from "../components/thank-you/CampaignPauseSuccess";
import ExtendCampaignConfirmationThankyouPage from "../components/thank-you/ExtendCampaignConfirmationThankyouPage";
import { USER_ROLE } from "@/config/constants";
import { getDashboardPath } from "@/helpers/utils";
import { ROUTES } from "@/helpers/routes/routePaths";
import { RRDDirectMailJoinCampThankYouPage } from "@/features/campaigns/direct-mail/rrd";
import {
  retainThankYouAccessValidation,
  releaseThankYouAccessValidation,
  validateThankYouAccess,
} from "@/shared/helpers/thankYouAccessHelper";

type ThankYouLocationState = {
  from?: string;
  is_redirect?: boolean;
  campaign_id?: string | number;
};

type DashboardNavButtonProps = {
  buttonText?: string;
  buttonLink?: string;
};

const ThankYouPage = () => {
  const { state } = useLocation();
  const locationState = state as ThankYouLocationState | null;
  const [searchParams] = useSearchParams();
  const { loggedInUserRole } = useContext(GlobalContext);
  const { is_redirect, campaign_id } = locationState || {};
  const queryType = searchParams.get("type");
  const routeStateFrom = locationState?.from;

  // Thank-you is a one-time post-action screen. A sessionStorage grant is set
  // right before navigation and must match the URL/state here. Query params alone
  // are not trusted, while a fresh grant keeps browser refresh usable.
  const accessGrant = useMemo(() => {
    return validateThankYouAccess({ queryType, stateFrom: routeStateFrom });
  }, [queryType, routeStateFrom]);
  const stateFrom = routeStateFrom || accessGrant?.stateFrom;
  const hasValidAccess = Boolean(accessGrant);

  useEffect(() => {
    retainThankYouAccessValidation();

    return () => {
      releaseThankYouAccessValidation();
    };
  }, []);

  const message = useMemo(() => {
    switch (stateFrom) {
      case "join_campaign_confirm":
        return <CampaignConfirmationThankyouPage />;
      case "rrd-join-campaign-confirm":
        return <RRDDirectMailJoinCampThankYouPage />;
      case "national-plus-up-confirm":
        return <RRDDirectMailJoinCampThankYouPage isNationalPlusUp />;
      case "package_selection":
        return (
          <ThankYou
            message={`<div>
                <p>
                  We've received your subscriptions!
                </p>       
                <p>      
                  You'll receive an email from us with a confirmation and helpful next steps.
                </p>
              </div>
            `}
          />
        );
      case "ms_activation_brief":
        return <MSActivationBriefThankYouMessage />;
      case "boost_campaign_confirm":
        return <BoostCampaignConfirmationThankyouPage />;
      case "campaign_pause":
        return <CampaignPauseSuccess />;
      case "extend_campaign":
        return <ExtendCampaignConfirmationThankyouPage />;

      default:
        return <ThankYou />;
    }
  }, [stateFrom]);

  if (!hasValidAccess) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  if (
    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
    (is_redirect || campaign_id)
  ) {
    return <CustomizeAds is_redirect={is_redirect} campaign_id={campaign_id} />;
  }

  return (
    <PageLayout className="thank-you-container mx-0 mx-sm-2">
      <div className="thank-you-page">{message}</div>
    </PageLayout>
  );
};

function MSActivationBriefThankYouMessage() {
  return (
    <div className="text-center">
      <Typography variant="h4" gutterBottom className="heading mb-5">
        Thank you for submitting your activation brief to get started with the
        Google Advertising Program.
      </Typography>
      <Typography gutterBottom className="mb-3">
        We will review your information and let you know if we have any
        questions or need additional information.
      </Typography>

      <Typography gutterBottom className="mb-3">
        We will provide you with your keyword and spend recommendation for
        approval within 5-7 business days, you will be notified via email when
        those are ready in platform for your review.
      </Typography>
      <Typography gutterBottom className="mb-3">
        If you have any questions about the process, you can reply to this email
        or reach out to a Morgan Stanley Marketing team member at
        localpaidsearch@morganstanley.com.
      </Typography>
      <Typography gutterBottom className="mb-3">
        To find additional information about the program and answers to
        Frequently Asked Questions, visit the program 3DR page.
      </Typography>

      <DashboardNavButton />
    </div>
  );
}

function DashboardNavButton({
  buttonText = "Back to My Dashboard",
  buttonLink = `${getDashboardPath()}`,
}: DashboardNavButtonProps) {
  return (
    <div className="d-flex-center">
      <Button
        component={Link}
        to={buttonLink}
        className="my-4"
        variant="contained"
        color="primary"
        size="large"
        startIcon={<Icon>laptop</Icon>}
        replace
      >
        {buttonText}
      </Button>
    </div>
  );
}

export default ThankYouPage;
